import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import ContactActions from 'app/shared/flux/actions/ContactActions';
import UserActions from 'app/shared/flux/actions/UserActions';
import UserSearchActions from 'app/shared/flux/actions/UserSearchActions';
import FetchListingActions from 'app/shared/flux/actions/FetchListingActions';
import AppActions from 'app/shared/flux/actions/AppActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import ConversationActions from 'app/shared/flux/actions/ConversationActions';
import NotificationActions from 'app/shared/flux/actions/NotificationActions';
import contactUtils from 'app/shared/utils/contactUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import loginUtils from 'app/shared/utils/loginUtils';
import routeUtils from 'app/shared/utils/routeUtils';
import { trackForRentContact } from 'app/client/tracking';
import {
  type ContactFormLeadTriggerObject,
  emitTrackInlineContactFormViewEvent,
  emitTrackInlineContactSubmitEvent,
  emitTrackModalContactFormViewEvent,
  emitTrackModalContactSubmitEvent,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { getListingType, getTrackingFunction } from 'app/shared/utils/clickstreamUtils';
import { useModal } from 'app/shared/contexts/ModalContext/useModal';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { hasSeenRenterProfilePopup, markRenterProfilePopupAsShown } from 'app/shared/utils/renterProfilePopupTracking';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type {
  ContactFormInputValues,
  ContactFormServerErrors,
  UseHaloContactSubmissionOptions,
  UseHaloContactSubmissionReturn,
} from './types';

const logger = getGlobalLogger();

type ValidationResult = {
  data: {
    email: { valid: boolean };
    phone: { valid: boolean };
  };
};

type UserDetailsResponse = {
  error?: boolean;
  message?: string;
  status?: 'ACCOUNT_DEACTIVATED' | 'USER_ALREADY_EXISTS' | string;
  success?: boolean;
  data?: {
    passwordStatus?: string;
  };
};

type DisplaySearchPromptResponse = {
  data: boolean;
};

export const useHaloContactSubmission = ({
  listing,
  onClose,
  requestToApply = false,
  triggerLocation = 'home_details_halo',
  triggerObject = 'home_details_component|modal_contact_form',
  formMode = 'modal',
}: UseHaloContactSubmissionOptions): UseHaloContactSubmissionReturn => {
  const isInlineForm = formMode === 'inline';
  const submitTriggerObject: ContactFormLeadTriggerObject = isInlineForm
    ? 'home_details_component|inline_form'
    : triggerObject;
  const dispatch = useDispatch();
  const { openModal } = useModal();
  const user = useSelector((state: RootReduxState) => state.user);
  const countryCode = useSelector((state: RootReduxState) => state.geolocation?.area?.countryCode);
  const pathname = useSelector((state: RootReduxState) => state.location?.current?.pathname ?? '');

  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ContactFormServerErrors>({
    emailError: '',
    phoneError: '',
    formError: '',
  });
  const [shouldDisplayPostContactSavedSearch, setShouldDisplayPostContactSavedSearch] = useState(false);

  // Check if should show saved search prompt on mount
  useEffect(() => {
    const useMapBounds = pathname ? routeUtils.isAreaUrl(pathname) || routeUtils.isPadOrBuildingUrl(pathname) : false;

    let isMounted = true;
    (
      dispatch(
        UserSearchActions.search.saved.displaySearchPromptInquiry({ shouldUseAreaBoundary: true, useMapBounds }),
      ) as unknown as Promise<DisplaySearchPromptResponse>
    )
      .then((res) => {
        if (isMounted) {
          setShouldDisplayPostContactSavedSearch(Boolean(res.data));
        }
        return res;
      })
      .catch((error: Error) => {
        if (isMounted) {
          dispatch(
            ErrorActions.errorHandler({
              error,
              errorLocation: 'hook.useHaloContactSubmission.useEffect',
              errorClass: 'userSearchActions',
            }),
          );
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fire form view tracking on mount
  useEffect(() => {
    if (isInlineForm) {
      emitTrackInlineContactFormViewEvent({ listing });
    } else {
      emitTrackModalContactFormViewEvent({
        listing,
        triggerLocation,
        triggerObject,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearErrors = useCallback(() => {
    setServerErrors({ emailError: '', phoneError: '', formError: '' });
  }, []);

  const handleFormSubmitFailure = useCallback((response: { message?: string } = {}) => {
    setIsLoading(false);
    setServerErrors((prev) => ({
      ...prev,
      formError: response.message || 'Failed to send inquiry.',
    }));
    logger?.error(response, 'Halo contact form submit failure');
  }, []);

  const getInquiryParams = useCallback(
    (inputValues: ContactFormInputValues) => {
      const listingPaid = Boolean(listing.trusted);
      const listingDemoted = Boolean(listing.details?.demoted);

      return {
        alias: listing.aliasEncoded,
        displayName: listing.displayName,
        address: listing.address.street,
        baths: inputValues.numBaths,
        beds: inputValues.numBeds,
        email: inputValues.userEmail,
        phone: numberUtils.formatPhoneNumber(inputValues.userPhoneNumber),
        name: inputValues.userName,
        text: contactUtils.getInquiryText({
          userMessage: inputValues.userMessage,
          lowIncomeMessage: inputValues.lowIncomeMessage,
          listing,
        }),
        textToCache: inputValues.userMessage,
        emailUser: true,
        defaultMessage: listing.contact.defaultInquiryMessage,
        paid: listingPaid,
        demoted: listingDemoted,
        modelId: null,
        requestToApply,
      };
    },
    [listing, requestToApply],
  );

  const handleTrackSubmitClick = useCallback(
    (rentalSubmitId: string) => {
      const listingType = getListingType(listing);
      const leadType = 'submit';

      if (isInlineForm) {
        emitTrackInlineContactSubmitEvent({
          listing,
          rentalSubmitId,
          triggerLocation,
        });
      } else {
        emitTrackModalContactSubmitEvent({
          listing,
          rentalSubmitId,
          triggerLocation,
          triggerObject,
          triggerSource: 'button_to_complete_contact_form',
        });
      }

      getTrackingFunction({ type: listingType, leadType })?.({
        listing,
        triggerLocation,
        triggerObject: submitTriggerObject,
        triggerSource: 'button_to_complete_contact_form',
      });
    },
    [listing, triggerLocation, triggerObject, isInlineForm, submitTriggerObject],
  );

  const continueContactInquiryFlow = useCallback(async () => {
    const { inquiryParams, rentalSubmitId } = contactUtils.getDelayedContactInquiry();

    if (!inquiryParams) {
      logger?.error(
        { inquiryParams, rentalSubmitId, listingAlias: listing.aliasEncoded },
        'Missing inquiry params in HALO contact',
      );
      handleFormSubmitFailure({ message: 'Something went wrong. Please try again later.' });
      return;
    }

    try {
      await dispatch(
        ContactActions.submitContact({
          listing: listing as unknown as Record<string, unknown>,
          inquiryParams,
          rentalSubmitId: rentalSubmitId ?? undefined,
        }),
      );

      trackForRentContact(listing);

      // Close the contact form spoke/overlay before opening post-contact modals
      onClose();

      setIsLoading(false);

      // Fetch similar listings for post-contact modals
      const availableSimilarListings = (await dispatch(
        FetchListingActions.fetchSimilarListingsFromApi({
          maloneLotIdEncoded: listing.maloneLotIdEncoded,
          unit: listing.unit,
          listingTypes: listing.listingType,
          limit: 5,
          excludeInquiredListings: true,
        }),
      )) as unknown as Array<ListingDetails>;

      const hasAvailableSimilarListings = availableSimilarListings && availableSimilarListings.length > 0;

      // Open post-contact modals in sequence, matching legacy flow
      if (shouldDisplayPostContactSavedSearch && user.loggedIn) {
        openModal({
          type: 'postContactSavedSearch',
          onClose: () => {
            if (hasAvailableSimilarListings) {
              openModal({
                type: 'contactSuccess',
                props: { similarListings: availableSimilarListings, listing },
                onClose: noop,
              });
            }
          },
        });
      } else if (listing.waitlisted && hasAvailableSimilarListings) {
        openModal({
          type: 'waitlistSuccess',
          props: { similarListings: availableSimilarListings },
          onClose: noop,
        });
      } else if (hasAvailableSimilarListings) {
        openModal({
          type: 'contactSuccess',
          props: { similarListings: availableSimilarListings, listing },
          onClose: noop,
        });
      }

      // Set optimistic message for untrusted listings
      if (!listing.trusted) {
        dispatch(ConversationActions.optimisticSetSentMessage(listing.aliasEncoded));
      }
    } catch (error) {
      dispatch(
        ErrorActions.errorHandler({
          error,
          errorLocation: 'hook.useHaloContactSubmission.continueContactInquiryFlow',
          errorClass: 'contactActions',
        }),
      );
      handleFormSubmitFailure();
    }
  }, [
    dispatch,
    listing,
    onClose,
    shouldDisplayPostContactSavedSearch,
    user.loggedIn,
    openModal,
    handleFormSubmitFailure,
  ]);

  const validateUserInfo = useCallback(
    async (inputValues: ContactFormInputValues) => {
      const email = inputValues.userEmail;
      const phone = inputValues.userPhoneNumber;
      const params = { listingAlias: listing.aliasEncoded, email, phone };

      setServerErrors({ emailError: '', phoneError: '', formError: '' });

      try {
        const res = (await dispatch(
          UserActions.inquiry.validateUserContactInfo(params),
        )) as unknown as ValidationResult;
        const { email: emailData, phone: phoneData } = res.data;

        if (!emailData.valid) {
          setServerErrors((prev) => ({ ...prev, emailError: 'Invalid email address' }));
        }

        if (!phoneData.valid) {
          setServerErrors((prev) => ({ ...prev, phoneError: 'Invalid phone number' }));
        }

        return { isValid: emailData.valid && phoneData.valid };
      } catch (error) {
        logger?.error(error, 'Halo contact validation error');
        setServerErrors((prev) => ({
          ...prev,
          formError: 'We could not validate your contact information. Please try again.',
        }));
        return { isValid: false };
      }
    },
    [dispatch, listing],
  );

  const handleSubmitForm = useCallback(
    async (inputValues: ContactFormInputValues) => {
      setIsLoading(true);

      const { isValid } = await validateUserInfo(inputValues);

      if (!isValid) {
        setIsLoading(false);
        return;
      }

      try {
        const rentalSubmitId = uuidv4();

        handleTrackSubmitClick(rentalSubmitId);

        await dispatch(AppActions.setAppStoreBool('userExists', false));
        const userDetails = (await dispatch(
          ContactActions.loginUser(user.loggedIn, inputValues.userEmail),
        )) as unknown as UserDetailsResponse;

        if (!userDetails) {
          handleFormSubmitFailure();
          return;
        }

        if (userDetails.error) {
          handleFormSubmitFailure({ message: userDetails.message });
          return;
        }

        if (userDetails.status === 'ACCOUNT_DEACTIVATED') {
          setIsLoading(false);
          setServerErrors((prev) => ({
            ...prev,
            emailError:
              'This email is associated with an account marked for deletion. Please use another email address.',
          }));
          return;
        }

        if (userDetails.status === 'USER_ALREADY_EXISTS') {
          dispatch(AppActions.setAppStoreBool('userExists', true));
        } else if (
          userDetails.success &&
          userDetails?.data?.passwordStatus?.toLowerCase() === 'freepass' &&
          countryCode &&
          loginUtils.isCanadianUser(countryCode, inputValues.userEmail)
        ) {
          dispatch(NotificationActions.email.updateEmailOptIn(true, 'inquiry'));
        }

        contactUtils.setDelayedContactInquiry({
          listing,
          inquiryParams: getInquiryParams(inputValues),
          rentalSubmitId,
          wasLoggedInAtStart: user.loggedIn,
        });

        if (hasSeenRenterProfilePopup()) {
          await continueContactInquiryFlow();
        } else {
          markRenterProfilePopupAsShown();
          openModal({
            type: 'postContactRenterProfile',
            onClose: continueContactInquiryFlow,
          });
        }
      } catch (error) {
        dispatch(
          ErrorActions.errorHandler({
            error,
            errorLocation: 'hook.useHaloContactSubmission.handleSubmitForm',
            errorClass: 'contactActions',
          }),
        );
        handleFormSubmitFailure();
      }
    },
    [
      dispatch,
      user,
      listing,
      countryCode,
      validateUserInfo,
      handleTrackSubmitClick,
      handleFormSubmitFailure,
      getInquiryParams,
      continueContactInquiryFlow,
      openModal,
    ],
  );

  const handlePhoneClick = useCallback(() => {
    const listingType = getListingType(listing);
    const leadType = 'phone';
    const triggerObject = 'home_details_component|modal_contact_form' as const;

    getTrackingFunction({ type: listingType, leadType })?.({
      listing,
      triggerLocation,
      triggerObject,
    });
  }, [listing]);

  return {
    handleSubmitForm,
    handlePhoneClick,
    isLoading,
    serverErrors,
    clearErrors,
  };
};
